import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Link as RouterLink, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Link,
  ListSubheader,
  MenuItem,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  IconArrowLeft,
  IconChevronLeft,
  IconChevronRight,
  IconChevronDown,
  IconMail,
  IconMessages,
  IconPlaneDeparture,
  IconRefresh,
  IconSend,
  IconUser,
  IconUserCheck,
} from "@tabler/icons";
import { toast } from "react-toastify";
import PageContainer from "src/components/container/PageContainer";
import { CommonAlert } from "src/components/alert/CommonAlert";
import useLeaveGuard from "src/components/shared/useLeaveGuard";
import { dataLocalStorage, getLocalStorage } from "src/helper/publicFunction";
import CustomerPanel, { PhoneLine } from "src/pages/clients/CustomerPanel";
import { updateRequest } from "src/pages/request/actions";
import { casesApi } from "./api";
import { STATUS, dateTime, dayOfInstant, initials, money, statusLabel, travelDay, tripWhen } from "./format";
import { dueWords } from "./listWords";
import Answers, { CopyValue } from "./Answers";
import CodeStrip from "./CodeStrip";
import { DecisionCard } from "./Decision";
import { failureWords } from "./decisions";
import HostCard, { PartyCards } from "./Host";
import Documents from "./Documents";
import Visa from "./Visa";
import Checks from "./Checks";
import ActionButton, { iconButtonSx } from "src/components/shared/ActionButton";
import { COPY_REMINDERS_MAX, copyRemindersSent, travelAnswer, travellerProgress } from "./rules";
import NextStep, { nextSteps, primaryAction } from "./NextStep";
import { ruleChips } from "src/pages/countries/visaRules";
import Payment from "./Payment";
import PromiseCard from "./PromiseCard";
import Together, { Insights } from "./Together";
// Checkout options: the speed and refusal protection bought, their refunds (builder S).
import CheckoutCase from "src/pages/checkout/CheckoutCase";
import Filing, { ReceiptViewer } from "./Filing";
import { wallWords } from "./filingWords";
import RecordDecision from "./RecordDecision";
import StatusDialog from "./StatusDialog";
import Timeline, { ownerSince } from "./Timeline";
import { PaymentChip, partnerName, paymentArranged, reportEmail, travellerLabel, travellerName, travellerNumber, useTravellerVisas, useUnpaidGuard } from "./workflow";
import { docAnchor, hasModifier, isTyping, railFits, readQueue, rememberOpened, reveal, toneColor, useNow, useTopBarHeight } from "./workspace";

// "18 min active on the form (about 6 h alone)": the customer's active time
// on the application (migration 022) and the visa's estimate of doing it
// alone. Not the same as "Where they spent time", which counts every page
// of the application, after submitting too: each says which (georges-28).
const minutesWords = (minutes) => (minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} h${minutes % 60 ? ` ${String(minutes % 60).padStart(2, "0")} min` : ""}`);
export function timeOnSafrati(data) {
  const seconds = Number(data?.time_spent_seconds);
  if (!Number.isFinite(seconds) || seconds <= 0) return "";
  const spent = seconds < 60 ? "under a minute" : minutesWords(Math.round(seconds / 60));
  const alone = Number(data?.diy_minutes) > 0 ? ` (about ${minutesWords(Number(data.diy_minutes))} alone)` : "";
  return `${spent} on Safrati${alone}`;
}

// The case header's figure: the same time, named as what it counts.
export const activeTimeWords = (data) => timeOnSafrati(data).replace(/ on Safrati/, " active on the form");

// Asking for new copies emails the customer (or, for a partner's case, asks
// the partner): show who gets it and what it lists before anything is sent.
function EmailNewCopiesDialog({ caseData, items, onClose, onSend }) {
  const many = caseData.travellers.length > 1;
  const partner = partnerName(caseData);
  const list = (
    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
      {items.map(({ doc, travellerNumber }) => (
        <Typography component="li" key={doc.upload.id} variant="body2" sx={{ mb: 0.5 }}>
          <b>
            {doc.name}
            {many ? ` (traveller ${travellerNumber})` : ""}
          </b>
          : {doc.upload.review_reason}
        </Typography>
      ))}
    </Box>
  );
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="case-email-title">
      <DialogTitle id="case-email-title">{partner ? `Ask ${partner} for new copies?` : "Email the customer for new copies?"}</DialogTitle>
      <DialogContent>
        {partner ? (
          <>
            <Typography variant="body2" mb={1}>
              {partner} asks the traveller for a new copy of:
            </Typography>
            {list}
            <Typography variant="body2" color="text.secondary" mt={1.5}>
              The new copy comes back here for review. No email is sent from Safrati.
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary">
              To
            </Typography>
            <Typography fontWeight={600} mb={1.5}>
              {caseData.customer.name ? `${caseData.customer.name} · ` : ""}
              {caseData.customer.email || "No email address on file"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Subject
            </Typography>
            <Typography fontWeight={600} mb={1.5}>
              We need a new copy for application {caseData.id}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              It asks them to upload a new copy on safrati.ai of:
            </Typography>
            {list}
            <Typography variant="body2" color="text.secondary" mt={1.5}>
              A team note records that the email was sent.
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <ActionButton variant="text" size="medium" onClick={onClose}>
          Don’t send
        </ActionButton>
        <ActionButton
          variant="contained"
          size="medium"
          startIcon={partner ? <IconSend size={18} /> : <IconMail size={18} />}
          busyLabel="Sending…"
          disabled={!partner && !caseData.customer.email}
          onClick={async () => {
            if (await onSend()) onClose();
          }}
        >
          {partner ? `Send to ${partner}` : "Send the email"}
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
}

EmailNewCopiesDialog.propTypes = {
  caseData: PropTypes.object.isRequired,
  items: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
};

// One team note: its writer can correct or delete it (nadia-26).
// A note the system wrote (the record of an email to the customer) is read
// only for everyone; an edited note says so (CMS review round 2, nadia-14).
export const noteSnippet = (body) => {
  const text = String(body || "").replace(/\s+/g, " ").trim();
  return text.length > 40 ? `${text.slice(0, 40)}…` : text;
};

function NoteItem({ note, mine, canEdit, onEdit, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(note.body);
  const own = canEdit && mine && !note.system;
  if (editing) {
    return (
      <Box>
        <TextField
          fullWidth
          multiline
          minRows={2}
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          inputProps={{ maxLength: 4000, "aria-label": `Edit the note “${noteSnippet(note.body)}”` }}
        />
        <Stack direction="row" spacing={1} justifyContent="flex-end" mt={1}>
          <ActionButton
            variant="text"
            onClick={() => {
              setText(note.body);
              setEditing(false);
            }}
          >
            Cancel
          </ActionButton>
          <ActionButton
            busyLabel="Saving…"
            disabled={!text.trim() || text.trim() === note.body}
            onClick={async () => {
              if (await onEdit(note, text.trim())) setEditing(false);
            }}
          >
            Save the note
          </ActionButton>
        </Stack>
      </Box>
    );
  }
  return (
    <Box>
      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
        {note.body}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary">
          {note.system ? `Written by the system for ${note.author || "the team"}` : note.author || "Team"} · {dateTime(note.created_at)}
          {note.edited_at ? ` · (edited ${dateTime(note.edited_at)})` : ""}
        </Typography>
        {own ? (
          <>
            <ActionButton variant="text" size="small" onClick={() => setEditing(true)} aria-label={`Edit your note “${noteSnippet(note.body)}”`}>
              Edit
            </ActionButton>
            <ActionButton variant="text" size="small" tone="destructive" onClick={() => onDelete(note)} aria-label={`Delete your note “${noteSnippet(note.body)}”`}>
              Delete
            </ActionButton>
          </>
        ) : null}
      </Stack>
    </Box>
  );
}

NoteItem.propTypes = { note: PropTypes.object.isRequired, mine: PropTypes.bool, canEdit: PropTypes.bool, onEdit: PropTypes.func, onDelete: PropTypes.func };

function Notes({ notes, canEdit, ready, onAdd, myId, onEdit, onDelete }) {
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  return (
    <Card variant="outlined" id="case-notes" tabIndex={-1} sx={{ outline: "none", scrollMarginTop: 180 }}>
      <CardContent>
        <Typography variant="h6" component="h2" mb={0.5}>
          Team notes
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={1.5}>
          Only the team sees these.
        </Typography>
        {canEdit && ready && (
          <Box mb={2}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              value={text}
              inputRef={inputRef}
              onChange={(e) => {
                setText(e.target.value);
                if (error) setError("");
              }}
              error={!!error}
              helperText={error || " "}
              placeholder="Appointment booked, call notes, what to check next…"
              inputProps={{ maxLength: 4000, "aria-label": "New note" }}
            />
            <Stack direction="row" justifyContent="flex-end">
              <ActionButton
                busyLabel="Adding…"
                onClick={async () => {
                  if (!text.trim()) {
                    setError("Write the note first.");
                    inputRef.current?.focus();
                    return;
                  }
                  if (await onAdd(text.trim())) setText("");
                }}
              >
                Add note
              </ActionButton>
            </Stack>
          </Box>
        )}
        {notes.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No notes yet.
          </Typography>
        )}
        <Stack spacing={1.5}>
          {notes.map((note) => (
            <NoteItem key={note.id} note={note} mine={myId != null && Number(note.admin_id) === Number(myId)} canEdit={canEdit && ready} onEdit={onEdit} onDelete={onDelete} />
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

Notes.propTypes = {
  notes: PropTypes.array,
  canEdit: PropTypes.bool,
  ready: PropTypes.bool,
  onAdd: PropTypes.func,
  myId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
};

function ProgressTile({ label, value, detail, done, percent, tone }) {
  return (
    <Box sx={{ p: 1.5, borderRadius: 2, border: 1, borderColor: "divider", minWidth: 0 }}>
      <Typography variant="body2" color="text.secondary" fontWeight={600}>
        {label}
      </Typography>
      {/* Wraps rather than cuts ("2 of 2 required upload…" at 200 % zoom, QA georges-25). */}
      <Typography fontWeight={700} sx={{ overflowWrap: "anywhere" }}>
        {value}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={percent}
        color={done ? "success" : "primary"}
        aria-hidden
        sx={{ my: 0.75, height: 6, borderRadius: 3, bgcolor: "grey.200" }}
      />
      <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: tone ? toneColor(tone) : "text.secondary" }}>{detail}</Typography>
    </Box>
  );
}

ProgressTile.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  detail: PropTypes.string,
  done: PropTypes.bool,
  percent: PropTypes.number,
  tone: PropTypes.string,
};

const NO_TRAVELLERS = [];
const CLOSED = ["cancelled", "rejected", "expired"];
const OPEN_STATUSES = ["pending", "under_review"];
const pct = (part, whole) => (whole ? Math.round((part / whole) * 100) : 100);

// Where this traveller stands: answers, documents, reviews.
function TravellerProgress({ traveller, ready }) {
  const p = travellerProgress(traveller);
  // The two states of a new copy, as everywhere on the case (QA nadia-15).
  const asked = traveller.documents.filter((d) => d.upload?.review_status === "needs_new");
  const notSent = asked.filter((d) => !d.upload.requested_at).length;
  const waiting = asked.length - notSent;
  const reviewParts = [
    p.review.toReview ? `${p.review.toReview} to review` : "",
    notSent ? `${notSent} new ${notSent === 1 ? "copy" : "copies"} needed, not sent yet` : "",
    waiting ? `${waiting} new ${waiting === 1 ? "copy" : "copies"} asked, waiting` : "",
  ].filter(Boolean);
  return (
    <Box
      component="section"
      aria-label="Progress"
      sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1, mb: 2.5 }}
    >
      <ProgressTile
        label="Answers"
        value={p.answers.required ? `${p.answers.given} of ${p.answers.required} required` : "None required"}
        detail={p.answers.missing ? `${p.answers.missing} missing` : "Complete"}
        tone={p.answers.missing ? "fail" : "pass"}
        done={!p.answers.missing}
        percent={pct(p.answers.given, p.answers.required)}
      />
      <ProgressTile
        label="Documents"
        // A product may mark none of its documents required (older products);
        // then say what was uploaded rather than "None required" next to
        // documents waiting for review.
        value={
          p.documents.required
            ? `${p.documents.uploaded} of ${p.documents.required} required uploaded`
            : p.review.uploads
            ? `${p.review.uploads} uploaded`
            : "Nothing uploaded"
        }
        detail={p.documents.missing ? `${p.documents.missing} not uploaded` : p.documents.required ? "All uploaded" : "None marked required"}
        tone={p.documents.missing ? "fail" : p.documents.required ? "pass" : undefined}
        done={!p.documents.missing}
        percent={pct(p.documents.uploaded, p.documents.required)}
      />
      <ProgressTile
        label="Review"
        value={p.review.uploads ? `${p.review.approved} of ${p.review.uploads} approved` : "Nothing uploaded"}
        detail={!ready ? "After database update 004" : reviewParts.length ? reviewParts.join(" · ") : p.review.uploads ? "All approved" : "—"}
        tone={ready && reviewParts.length ? "warn" : ready && p.review.uploads ? "pass" : undefined}
        done={ready && p.review.uploads > 0 && !reviewParts.length}
        percent={pct(p.review.approved, p.review.uploads)}
      />
    </Box>
  );
}

TravellerProgress.propTypes = { traveller: PropTypes.object.isRequired, ready: PropTypes.bool };

// A traveller's own status on their tab, when the travellers' statuses
// differ (nadia-05: Maya moved to In review alone showed nowhere).
export const mixedStatuses = (travellers = []) => new Set(travellers.map((t) => t.status).filter(Boolean)).size > 1;
function TravellerStatusChip({ traveller }) {
  const status = STATUS[traveller.status] ?? { label: traveller.status, color: "default" };
  return <Chip size="small" color={status.color} label={status.label} aria-label={`Status: ${status.label}`} />;
}

TravellerStatusChip.propTypes = { traveller: PropTypes.object.isRequired };

// One chip on a traveller's tab: what is left for them, most urgent first.
function TabChip({ traveller }) {
  const p = travellerProgress(traveller);
  const missing = p.answers.missing + p.documents.missing;
  if (missing) return <Chip size="small" color="warning" label={`${missing} missing`} />;
  if (p.review.toReview) return <Chip size="small" variant="outlined" color="primary" label={`${p.review.toReview} to review`} />;
  if (p.review.newCopy) {
    const notSent = traveller.documents.some((d) => d.upload?.review_status === "needs_new" && !d.upload.requested_at);
    return <Chip size="small" variant="outlined" color="warning" label={notSent ? "New copy needed" : "Waiting for new copy"} />;
  }
  if (p.review.uploads) return <Chip size="small" variant="outlined" color="success" label="All approved" />;
  return null;
}

TabChip.propTypes = { traveller: PropTypes.object.isRequired };

function CaseNav({ queue, index, onMove, compact = false }) {
  if (!queue || index < 0) return null;
  const first = index === 0;
  const last = index === queue.ids.length - 1;
  const note = last ? (queue.partial ? "Last on this page" : "Last in the list") : first ? "First in the list" : queue.label;
  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
      <Tooltip title="Previous case (K)">
        <span>
          <IconButton aria-label="Previous case" aria-keyshortcuts="K" disabled={first} onClick={() => onMove(-1)} sx={iconButtonSx}>
            <IconChevronLeft size={20} />
          </IconButton>
        </span>
      </Tooltip>
      {compact ? (
        <Typography variant="caption" fontWeight={700} sx={{ whiteSpace: "nowrap" }} title={note}>
          {index + 1}/{queue.ids.length}
        </Typography>
      ) : (
        <Box sx={{ textAlign: "center", minWidth: 84 }}>
          <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: "nowrap" }}>
            {index + 1} of {queue.ids.length}
          </Typography>
          <Typography sx={{ fontSize: "0.6875rem", color: "text.secondary", maxWidth: 140 }} noWrap title={queue.label}>
            {note}
          </Typography>
        </Box>
      )}
      <Tooltip title="Next case (J)">
        <span>
          <IconButton aria-label="Next case" aria-keyshortcuts="J" disabled={last} onClick={() => onMove(1)} sx={iconButtonSx}>
            <IconChevronRight size={20} />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
}

CaseNav.propTypes = { queue: PropTypes.object, index: PropTypes.number, onMove: PropTypes.func, compact: PropTypes.bool };

// The case's sections, as links in the sticky header (QA nadia-06,
// karim-17): Documents · Answers · Payment · Filing · Notes · Timeline.
// Real links (#case-documents), so they also work from the keyboard.
function SectionBar({ sections, onJump }) {
  return (
    <Box
      component="nav"
      aria-label="Sections of this case"
      sx={{ display: "flex", gap: 0.75, overflowX: "auto", mt: 1, mx: -0.5, px: 0.5, py: 0.25, scrollbarWidth: "thin" }}
    >
      {sections.map(({ id, label }) => (
        <Box
          component="a"
          key={id}
          href={`#${id}`}
          onClick={(event) => {
            event.preventDefault();
            onJump(id);
          }}
          sx={{
            flexShrink: 0,
            display: "inline-flex",
            alignItems: "center",
            px: 1.5,
            minHeight: 32,
            borderRadius: 999,
            border: 1,
            borderColor: "divider",
            color: "text.primary",
            fontSize: "0.8125rem",
            fontWeight: 600,
            textDecoration: "none",
            whiteSpace: "nowrap",
            "&:hover": { bgcolor: "primary.light", borderColor: "primary.main" },
            "&:focus-visible": { outline: (t) => `2px solid ${t.palette.primary.main}`, outlineOffset: "2px" },
            "@media (pointer: coarse)": { minHeight: 44 },
          }}
        >
          {label}
        </Box>
      ))}
    </Box>
  );
}

SectionBar.propTypes = { sections: PropTypes.array.isRequired, onJump: PropTypes.func.isRequired };

// The filing's appointment, for the summary at the top (QA karim-17): when
// (Beirut time), where and the booking reference, or null.
export function appointmentOf(data) {
  const steps = (data?.filing?.started && data.filing.steps) || [];
  const step = steps.find((s) => s.kind === "appointment" && s.scheduled_at && !s.done) || steps.find((s) => s.kind === "appointment" && s.scheduled_at);
  if (!step) return null;
  return [`${wallWords(step.scheduled_at)} (Beirut time)`, step.place, step.reference && `booking ${step.reference}`].filter(Boolean).join(" · ");
}

// Who the case is for when the account has no name (QA nadia-13): the first
// traveller's name, else the email.
export const caseName = (data) => data.customer?.name || data.travellers?.[0]?.name || data.customer?.email || "Customer";

const reducedMotion = () => !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

function Case({ id }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const wide = useMediaQuery((theme) => theme.breakpoints.up("lg"));
  const phone = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  // A short window (200 % zoom, a phone held sideways): the header scrolls
  // away rather than take most of the screen.
  const short = useMediaQuery("(max-height: 560px)");
  const topBar = useTopBarHeight();
  const privileges = getLocalStorage(dataLocalStorage.privileges) || {};
  const canEdit = Number(privileges["23"]?.access_edit) === 1;
  const canChats = Number(privileges["23"]?.access_read) === 1;
  const [state, setState] = useState({ loading: true, failed: false, notFound: false, data: null });
  const alert = CommonAlert();
  // Who is signed in: their own team notes can be edited and deleted.
  const signedInId = getLocalStorage(dataLocalStorage.userinfo)?.id ?? null;
  const [agents, setAgents] = useState([]);
  // ?traveller=2 (from "Cases by traveller", or a number like 1611-2) opens
  // that traveller's tab.
  const [tab, setTab] = useState(() => Math.max((Number(params.get("traveller")) || 1) - 1, 0));
  const [statusOpen, setStatusOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [jump, setJump] = useState(null);
  // "Record the decision": { outcome?, step? } while open.
  const [decide, setDecide] = useState(null);
  const [openDoc, setOpenDoc] = useState(null);
  const [customerOpen, setCustomerOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const visas = useTravellerVisas(state.data?.travellers || NO_TRAVELLERS);
  const { guard, dialog: unpaidDialog } = useUnpaidGuard(paymentArranged(state.data));
  // A code is fresh for an hour: the clock moves it to the timeline.
  const now = useNow(30000);
  const [decisionsUnavailable, setDecisionsUnavailable] = useState(false);
  const [receiptOpen, setReceiptOpen] = useState(null);
  // New copies marked but not emailed: leaving the case asks whether to
  // email the customer now (CMS review round 2, nadia-31).
  const unsentCopies = (state.data?.travellers || []).reduce(
    (n, t) => n + (t.documents || []).filter((d) => d.upload?.review_status === "needs_new" && !d.upload.requested_at).length,
    0,
  );
  const leaving = useLeaveGuard(canEdit && unsentCopies > 0 && !emailOpen);
  // Most urgent first. The code goes in the strip at the top of the case;
  // the next step card shows the first of the rest.
  const steps = state.data ? nextSteps(state.data, visas, { now }) : [];
  const freshCode = steps.find((s) => s.kind === "code")?.code || null;
  const step = steps.find((s) => s.kind !== "code") || null;
  const stepKind = step?.kind ?? null;
  const lastStep = useRef(stepKind);

  // Approving the last document turns "review" into "file it": bring that
  // banner into view rather than leaving it off-screen.
  useEffect(() => {
    if (lastStep.current === "review" && stepKind === "file") reveal("case-next-step");
    lastStep.current = stepKind;
  }, [stepKind]);

  // The right column sticks under the case header; it needs the header's height.
  const observer = useRef(null);
  const header = useCallback((node) => {
    observer.current?.disconnect();
    if (!node || typeof ResizeObserver === "undefined") return;
    observer.current = new ResizeObserver(() => setHeaderHeight(node.offsetHeight));
    observer.current.observe(node);
  }, []);

  // The right column sticks only while all of it fits under the header;
  // taller, it scrolls with the page. It used to scroll inside itself, which
  // hid the Timeline and cut the top card off (QA F05).
  const [railHeight, setRailHeight] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(() => window.innerHeight);
  const railObserver = useRef(null);
  const rail = useCallback((node) => {
    railObserver.current?.disconnect();
    if (!node || typeof ResizeObserver === "undefined") return;
    railObserver.current = new ResizeObserver(() => setRailHeight(node.offsetHeight));
    railObserver.current.observe(node);
  }, []);
  useEffect(() => {
    const onResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // The inbox view this case was opened from, for Next / Previous.
  const queue = useMemo(() => {
    const candidate = location.state?.queue || readQueue();
    return candidate && Array.isArray(candidate.ids) ? candidate : null;
  }, [location.state]);
  const position = queue ? queue.ids.indexOf(Number(id)) : -1;
  const backTo = `/cases${queue?.search || ""}`;

  useEffect(() => {
    rememberOpened(id);
  }, [id]);

  // A case that opens puts the keyboard on its name, not on the menu: Tab
  // goes on into the case (karim-25). Not when something has the focus.
  const headingRef = useRef(null);
  const focusedFor = useRef(null);
  const openedId = state.data?.id;
  useEffect(() => {
    if (!openedId || focusedFor.current === openedId) return;
    focusedFor.current = openedId;
    const active = document.activeElement;
    if (active && active !== document.body && !active.closest?.("nav, header, [role='navigation']")) return;
    headingRef.current?.focus({ preventScroll: true });
  }, [openedId]);

  const load = useCallback(
    (quiet) => {
      if (!quiet) setState((s) => ({ ...s, loading: true, failed: false }));
      // A refresh behind the page toasts its failure; a first load says it inline.
      // Answers with the case (null when it didn't load).
      return casesApi
        .get(id, dispatch, !!quiet)
        .then((response) => {
          setState({ loading: false, failed: false, notFound: false, data: response?.data });
          return response?.data || null;
        })
        .catch((err) => {
          setState((s) => ({
            ...s,
            loading: false,
            failed: !s.data,
            notFound: /not found/i.test(err?.message || ""),
          }));
          return null;
        });
    },
    [id, dispatch],
  );

  useEffect(() => {
    load();
  }, [load]);

  // Opened by a traveller's own request id (/cases/1612): that traveller's tab.
  const loadedId = state.data?.id;
  useEffect(() => {
    if (!loadedId || Number(id) === loadedId || params.get("traveller")) return;
    const index = state.data.travellers.findIndex((t) => t.request_id === Number(id));
    if (index > 0) setTab(index);
    // Only when a case first arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedId]);

  // The API's next step was a code that is now over an hour old on this
  // clock: read the case again (once per code) for what comes next.
  const apiCode = state.data?.next_step?.kind === "use_code" ? state.data.next_step.sent_at || "" : null;
  const apiCodeStale = apiCode !== null && !freshCode;
  const reloadedFor = useRef(null);
  useEffect(() => {
    if (!apiCodeStale || reloadedFor.current === apiCode) return;
    reloadedFor.current = apiCode;
    load(true);
  }, [apiCodeStale, apiCode, load]);

  useEffect(() => {
    casesApi
      .agents(dispatch)
      .then((response) => setAgents(response?.data?.agents || []))
      .catch(() => setAgents([]));
  }, [dispatch]);

  const move = useCallback(
    (by) => {
      if (!queue || position < 0) return;
      const next = queue.ids[position + by];
      if (next) navigate(`/cases/${next}`, { state: { queue } });
    },
    [queue, position, navigate],
  );

  // j / k for the next and previous case, u back to the list (Gmail's keys).
  useEffect(() => {
    const onKey = (event) => {
      if (event.defaultPrevented || hasModifier(event) || isTyping(event)) return;
      if (document.querySelector(".MuiDialog-root")) return;
      if (event.key === "j") move(1);
      else if (event.key === "k") move(-1);
      else if (event.key === "u") navigate(backTo);
      else return;
      event.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [move, navigate, backTo]);

  // A check's "Go to" for a document: switch to that traveller, then show it.
  useEffect(() => {
    if (!jump || jump.type !== "doc") return undefined;
    const frame = requestAnimationFrame(() => reveal(docAnchor(jump.requestId, jump.documentId)));
    return () => cancelAnimationFrame(frame);
  }, [jump]);

  const data = state.data;

  // Ctrl K's "Assign this case to me" (CMS review round 2, karim-25): the
  // finder asks, the case screen does it the way its own button does.
  const assignMe = useRef(null);
  useEffect(() => {
    const onCommand = (event) => {
      if (event.detail?.action === "assign-to-me") assignMe.current?.();
    };
    window.addEventListener("safrati:case", onCommand);
    return () => window.removeEventListener("safrati:case", onCommand);
  }, []);

  if (state.loading && !data) {
    return (
      <PageContainer title="Case" description="Visa application">
        <Skeleton variant="rounded" height={64} sx={{ mb: 2, mt: 1 }} />
        <Skeleton variant="rounded" height={88} sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} lg={8}>
            <Skeleton variant="rounded" height={480} />
          </Grid>
          <Grid item xs={12} lg={4}>
            <Skeleton variant="rounded" height={240} />
          </Grid>
        </Grid>
      </PageContainer>
    );
  }

  if (!data) {
    return (
      <PageContainer title="Case" description="Visa application">
        <Alert
          severity={state.notFound ? "warning" : "error"}
          sx={{ mt: 1, alignItems: "center" }}
          action={
            <Stack direction="row" spacing={1}>
              {!state.notFound && (
                <ActionButton startIcon={<IconRefresh size={16} />} onClick={() => load()}>
                  Try again
                </ActionButton>
              )}
              <ActionButton variant="text" onClick={() => navigate(backTo)}>
                Back to cases
              </ActionButton>
            </Stack>
          }
        >
          {state.notFound ? "This application does not exist." : "The case did not load."}
        </Alert>
      </PageContainer>
    );
  }

  const status = STATUS[data.status] ?? { label: data.status, color: "default" };
  // Travellers in different statuses: the header says "Mixed", each tab its own.
  const mixed = mixedStatuses(data.travellers);
  const mixedWords = mixed ? data.travellers.map((t) => `${travellerName(t)}: ${statusLabel(t.status)}`).join(" · ") : "";
  // The day promised to the customer (rania-10).
  const due = dueWords(data.due);
  const travellerIndex = Math.min(tab, data.travellers.length - 1);
  const traveller = data.travellers[travellerIndex];
  const name = caseName(data);
  const draft = data.status === "incomplete";
  // Documents marked "new copy needed": not sent yet, or asked and waited on.
  const askedFor = data.travellers.flatMap((t) =>
    t.documents.filter((d) => d.upload?.review_status === "needs_new").map((doc) => ({ doc, travellerNumber: t.number })),
  );
  const notEmailed = askedFor.filter(({ doc }) => !doc.upload.requested_at).length;
  const lastEmailed = askedFor
    .map(({ doc }) => doc.upload.requested_at)
    .filter(Boolean)
    .sort()
    .pop();
  const me = agents.find((a) => a.is_me);
  const ready = data.workspace_ready;
  // The owner always appears in the list, even if the team list did not load.
  const ownerOptions = data.assigned && !agents.some((a) => a.id === data.assigned.id) ? [...agents, { ...data.assigned }] : agents;
  const since = ownerSince(data);
  // A partner's case asks the partner, which asks its traveller; no email.
  const partner = partnerName(data);
  const total = data.payment?.amount ?? data.total;
  const unpaid = !draft && !paymentArranged(data) ? Number(total) : null;

  const assign = async (adminId) => {
    setAssigning(true);
    try {
      const res = await casesApi.assign(data.id, adminId, dispatch);
      // A submitted application's customer is emailed who is reviewing it
      // (API: data.emailed true; false when the email failed; null when none was due).
      const emailed = res?.data?.emailed;
      if (emailed === true) toast.success("Owner saved. The customer was emailed who is reviewing it.");
      else if (emailed === false) toast.warn("Owner saved, but the email telling the customer who is reviewing it failed.");
      else toast.success("Owner saved.");
      await load(true);
    } catch {
      /* the toast says why */
    } finally {
      setAssigning(false);
    }
  };
  assignMe.current = () => {
    if (canEdit && me && data.assigned?.id !== me.id) assign(me.id);
    else if (data.assigned?.id === me?.id) toast.info("This case is already yours.");
  };

  const go = (index, target) => {
    if (!target) return;
    setTab(index);
    setJump({ ...target, requestId: data.travellers[index].request_id, n: Date.now() });
  };

  // A section from the bar: its top just under the sticky header.
  const jumpTo = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (!element) return;
    element.style.scrollMarginTop = `${topBar + (short ? 0 : headerHeight) + 12}px`;
    element.scrollIntoView({ behavior: reducedMotion() ? "auto" : "smooth", block: "start" });
    element.focus?.({ preventScroll: true });
  };

  // Who owns the case. In the sticky bar on wide screens; below lg (tablets
  // and phones) it sits in the summary card and the bar shows only the
  // owner's initials, so the bar keeps to one calm line.
  const ownerControls = ready && (
    <>
      <TextField
        select
        size="small"
        label={assigning ? "Owner (saving…)" : "Owner"}
        value={data.assigned?.id ?? ""}
        disabled={!canEdit || assigning}
        onChange={(e) => assign(e.target.value === "" ? null : Number(e.target.value))}
        SelectProps={{ displayEmpty: true }}
        InputLabelProps={{ shrink: true }}
        sx={{ minWidth: { xs: "100%", sm: 190 }, flex: { xs: "1 1 100%", sm: "none" }, "& .MuiInputBase-root": { minHeight: 40 } }}
      >
        {/* What picking a name does, and each person's load, as in the bulk dialog (rania-14). */}
        <ListSubheader sx={{ lineHeight: 1.4, py: 1, whiteSpace: "normal", maxWidth: 320, fontWeight: 400, color: "text.secondary" }}>
          {OPEN_STATUSES.includes(data.status) && !partner
            ? "Picking a name saves at once, and the customer is emailed who is reviewing it."
            : "Picking a name saves at once. Nobody is emailed."}
        </ListSubheader>
        <MenuItem value="">Nobody yet</MenuItem>
        {ownerOptions.map((a) => (
          <MenuItem key={a.id} value={a.id}>
            {a.name}
            {a.is_me ? " (me)" : ""}
            {a.open != null && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                {a.open} open{a.late ? `, ${a.late} waiting 7+ days` : ""}
              </Typography>
            )}
          </MenuItem>
        ))}
      </TextField>
      {canEdit && me && data.assigned?.id !== me.id && (
        <ActionButton startIcon={<IconUserCheck size={16} />} busy={assigning} busyLabel="Assigning…" onClick={() => assign(me.id)}>
          Assign to me
        </ActionButton>
      )}
    </>
  );

  const ownerBadge = ready && (
    <Tooltip title={data.assigned ? `Owner: ${data.assigned.name}` : "No owner yet"}>
      <Avatar
        role="img"
        aria-label={data.assigned ? `Owner: ${data.assigned.name}` : "No owner yet"}
        sx={{
          width: 32,
          height: 32,
          fontSize: 13,
          ml: "auto !important",
          flexShrink: 0,
          ...(data.assigned
            ? { bgcolor: "primary.main" }
            : { bgcolor: "transparent", color: "text.secondary", border: 1, borderStyle: "dashed", borderColor: "divider" }),
        }}
      >
        {data.assigned ? initials(data.assigned.name) : <IconUser size={18} aria-hidden />}
      </Avatar>
    </Tooltip>
  );

  const checks = <Checks data={data} onGo={go} />;

  const reviewFirst = () => {
    for (let i = 0; i < data.travellers.length; i += 1) {
      const doc = data.travellers[i].documents.find((d) => d.upload && !d.upload.review_status);
      if (doc) {
        go(i, { type: "doc", documentId: doc.document_id });
        setOpenDoc({ requestId: data.travellers[i].request_id, documentId: doc.document_id, n: Date.now() });
        return;
      }
    }
  };
  const onCalled = async () => {
    try {
      await casesApi.called(data.id, dispatch);
      toast.success(`Marked as called: case #${data.id}.`);
      await load(true);
    } catch (err) {
      toast.error(failureWords(err, "The call wasn’t saved. Try again."));
    }
  };
  // What the next step's buttons do, in the box and in the header.
  const handlers = {
    onStatus: (initial) => setStatusOpen({ initial }),
    onDecide: () => setDecide({}),
    onAddVisa: () => setDecide({ outcome: "approved", step: "visa" }),
    onReview: reviewFirst,
    onCalled,
    onReceipt: setReceiptOpen,
  };
  const primary = canEdit ? primaryAction(step, handlers) : null;
  // NextStep draws a card (with the main button) for these steps.
  const nextStepShown = !!step && canEdit && step.kind !== "code";
  // When to apply and how this visa is submitted (migration 017), set on the country page.
  const visaRules = data.visa_rules ? ruleChips(data.visa_rules) : [];
  const nextStepAlert = (
    <Stack spacing={1}>
      <NextStep step={step} travellers={data.travellers.length} partner={partner} customer={name} canEdit={canEdit} unpaid={unpaid} {...handlers} />
      {visaRules.length ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75 }}>
          <Typography variant="body2" color="text.secondary">
            This visa:
          </Typography>
          {visaRules.map((label) => (
            <Chip key={label} size="small" variant="outlined" label={label} />
          ))}
        </Box>
      ) : null}
    </Stack>
  );
  // The decision record (migration 023) on an approved or refused case.
  const decisionCard = (
    <DecisionCard
      caseData={data}
      canEdit={canEdit}
      unavailable={decisionsUnavailable}
      onRecord={(outcome) => setDecide({ outcome })}
      onEdit={(outcome) => setDecide({ outcome })}
    />
  );
  // A closed case (cancelled, refused, expired) has no trip to count down to.
  const trip = CLOSED.includes(data.status) ? null : tripWhen(travelDay(travelAnswer(data)), undefined, { year: true });
  // Red while the work is still ahead: open, or approved with a visa to send.
  const tripUrgent = !!trip?.soon && (["pending", "under_review"].includes(data.status) || step?.kind === "send");
  const appointment = appointmentOf(data);
  const progress = data.travellers.map(travellerProgress);
  const missingAnswers = progress.reduce((n, p) => n + p.answers.missing, 0);
  const missingDocs = progress.reduce((n, p) => n + p.documents.missing, 0);
  const toReview = progress.reduce((n, p) => n + p.review.toReview, 0);
  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
  const whatsMissing = [
    missingAnswers && plural(missingAnswers, "answer", "answers"),
    missingDocs && plural(missingDocs, "document", "documents"),
  ].filter(Boolean);
  const sections = [
    { id: "case-documents", label: "Documents" },
    { id: "case-answers", label: "Answers" },
    { id: "case-payment", label: "Payment" },
    ...(data.filing?.migrated ? [{ id: "case-filing", label: "Filing" }] : []),
    { id: "case-notes", label: "Notes" },
    { id: "case-timeline", label: "Timeline" },
  ];

  const notEmailedText = `${notEmailed === 1 ? "1 new copy" : `${notEmailed} new copies`} needed, not sent yet`;
  const sendCopiesLabel = partner ? `Send to ${partner}` : "Email the customer";
  const SendIcon = partner ? IconSend : IconMail;
  // The server's follow-ups since (API docs/copy-reminders.md).
  const reminders = partner ? { count: 0, last: null } : copyRemindersSent(data);
  const unanswered = notEmailed === 0 && reminders.count >= COPY_REMINDERS_MAX;
  const remindedText = reminders.count
    ? ` Reminded by email ${reminders.count === 1 ? "once" : "twice"}, last on ${dateTime(reminders.last)}.`
    : "";
  const newCopiesAlert = askedFor.length > 0 && canEdit && (
    <Alert severity={notEmailed > 0 || unanswered ? "warning" : "info"} sx={{ "& .MuiAlert-message": { width: "100%" } }}>
      <Typography variant="body2" fontWeight={notEmailed > 0 || unanswered ? 700 : 400}>
        {notEmailed > 0
          ? `${notEmailedText}: the ${partner ? "traveller" : "customer"} doesn’t know yet.`
          : `${askedFor.length === 1 ? "New copy" : `${askedFor.length} new copies`} asked on ${dayOfInstant(lastEmailed)}, waiting for the ${
              partner ? partner : "customer"
            }.${remindedText} ${unanswered ? "No new copy yet: call or message them." : ""}`.trim()}
      </Typography>
      <ActionButton
        variant={notEmailed > 0 ? "contained" : "outlined"}
        startIcon={<SendIcon size={16} />}
        onClick={() => setEmailOpen(true)}
        sx={{ mt: 1.25 }}
      >
        {notEmailed > 0 ? sendCopiesLabel : partner ? "Send again" : "Email again"}
      </ActionButton>
    </Alert>
  );

  // Below lg the right column is not beside the work: a bar at the bottom
  // keeps the unsent email in view until it goes.
  const emailReminder = canEdit && notEmailed > 0 && !wide && !emailOpen && (
    <Box
      role="status"
      sx={{
        position: "sticky",
        bottom: 0,
        zIndex: 5,
        mx: { xs: -2, sm: -3 },
        mt: 2,
        px: { xs: 2, sm: 3 },
        py: 1.25,
        bgcolor: "background.paper",
        borderTop: 1,
        borderColor: "divider",
        boxShadow: "0 -4px 12px rgba(19, 36, 59, 0.08)",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        flexWrap: "wrap",
      }}
    >
      <Typography variant="body2" fontWeight={700} sx={{ flex: "1 1 160px" }}>
        {notEmailedText}
      </Typography>
      <ActionButton variant="contained" startIcon={<SendIcon size={16} />} onClick={() => setEmailOpen(true)}>
        {sendCopiesLabel}
      </ActionButton>
    </Box>
  );

  // Every status but Approved and Refused (those are recorded with the
  // decision): the whole application, or one traveller as the classic page
  // allowed.
  const saveStatus = async ({ scope, status: next, notify, reason }) => {
    const one = scope === "case" ? null : data.travellers.find((t) => t.request_id === scope);
    const run = async () => {
      try {
        const response = one
          ? await updateRequest({ id: one.request_id, status: next, expireAt: visas.byId[one.request_id]?.expire_at, notify, quiet: true }, dispatch)
          : await casesApi.status(data.id, next, reason, dispatch, notify);
        const emailed = response?.data?.emailed;
        const sentTo = response?.data?.partner || partner;
        const whom = one ? travellerLabel(data, one) : `Case #${data.id}`;
        if (sentTo) {
          if (response?.data?.partner_notified === false)
            toast.warn(`${whom} is ${statusLabel(next)}, but the status did not reach ${sentTo}. It will be retried; tell them another way if it’s urgent.`);
          else toast.success(`${whom} is ${statusLabel(next)}. ${notify ? `${sentTo} tells the traveller.` : `${sentTo} gets it but won’t tell the traveller.`}`);
        } else {
          toast.success(`${whom} is ${statusLabel(next)}. ${emailed ?? notify ? "The customer was emailed." : "No email was sent."}`);
          await reportEmail({ asked: notify, emailed, caseId: data.id, what: `status ${statusLabel(next)}`, dispatch });
        }
        await Promise.all([load(true), visas.reload()]);
        return true;
      } catch (err) {
        // A rule the server explains (4xx) is added to the message;
        // anything else is only "it wasn't changed".
        const rule = err?.status >= 400 && err?.status < 500 && err.status !== 401 ? err.message : "";
        return { failed: true, reason: rule };
      }
    };
    // Filing or approving an unpaid application asks first.
    return next === "under_review" || next === "approved" ? guard(run) : run();
  };

  const metaLine = [
    data.travellers.length === 1 ? "1 traveller" : `${data.travellers.length} travellers`,
    money(total),
    // A draft's date is when it was started (QA nadia-13).
    data.submitted_at ? (draft ? `started ${dateTime(data.submitted_at)} · not submitted yet` : `submitted ${dateTime(data.submitted_at)}`) : draft ? "not submitted yet" : "",
    data.nationality ? `applying as ${data.nationality}` : "",
    activeTimeWords(data),
  ].filter(Boolean);

  return (
    <PageContainer title={`Case #${data.id} · ${name}`} description="Visa application">
      {/* The header stays in view: who, what state, who owns it, what to do. */}
      <Box
        ref={header}
        sx={{
          position: short ? "static" : "sticky",
          top: topBar,
          zIndex: 6,
          bgcolor: "background.default",
          mx: { xs: -2, sm: -3 },
          px: { xs: 2, sm: 3 },
          pt: { xs: 0.5, sm: 1 },
          pb: { xs: 1, sm: 1.5 },
        }}
      >
        <Card
          variant="outlined"
          component="header"
          sx={{ p: { xs: 1, sm: 1.5 }, boxShadow: "0 2px 10px rgba(19, 36, 59, 0.06)", overflow: "visible" }}
        >
          <Stack direction="row" alignItems="center" columnGap={1.5} rowGap={1} flexWrap="wrap">
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, flex: { xs: "1 1 0", sm: "1 1 280px" } }}>
              {phone ? (
                <IconButton component={RouterLink} to={backTo} aria-label="Back to cases" aria-keyshortcuts="U" sx={iconButtonSx}>
                  <IconArrowLeft size={20} />
                </IconButton>
              ) : (
                <ActionButton variant="text" component={RouterLink} to={backTo} startIcon={<IconArrowLeft size={16} />} aria-keyshortcuts="U">
                  Cases
                </ActionButton>
              )}
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" alignItems="baseline" spacing={1} sx={{ minWidth: 0 }}>
                  <Typography
                    component="h1"
                    ref={headingRef}
                    tabIndex={-1}
                    sx={{ fontSize: "1.0625rem", fontWeight: 700, lineHeight: 1.3, outline: "none" }}
                    noWrap
                    title={name}
                  >
                    {name}
                  </Typography>
                  <Typography sx={{ fontFamily: "monospace", fontWeight: 700, color: "text.secondary", whiteSpace: "nowrap" }}>#{data.id}</Typography>
                </Stack>
                {/* Wraps to a second line rather than cut the trip date (nadia-11). */}
                <Stack direction="row" columnGap={0.75} rowGap={0.25} alignItems="center" flexWrap="wrap" mt={0.25} sx={{ minWidth: 0 }}>
                  {phone && canEdit ? (
                    // On a phone the status itself is the button: one header row.
                    <Chip
                      size="small"
                      color={status.color}
                      label={mixed ? `${status.label} · mixed` : status.label}
                      onClick={() => setStatusOpen({})}
                      onDelete={() => setStatusOpen({})}
                      deleteIcon={<IconChevronDown size={14} aria-hidden />}
                      aria-label={`Status: ${status.label}. Change status`}
                    />
                  ) : (
                    <Tooltip title={mixedWords}>
                      <Chip size="small" color={status.color} label={mixed ? `${status.label} · mixed` : status.label} aria-label={mixed ? `Status: mixed. ${mixedWords}` : `Status: ${status.label}`} />
                    </Tooltip>
                  )}
                  {!draft && <PaymentChip caseData={data} />}
                  {trip && (
                    <Typography
                      variant="body2"
                      title={`Travels ${trip.date}, ${trip.when}`}
                      sx={{
                        whiteSpace: "nowrap",
                        display: { xs: "none", sm: "flex" },
                        alignItems: "center",
                        gap: 0.5,
                        color: tripUrgent ? "error.main" : "text.secondary",
                        fontWeight: tripUrgent ? 700 : 400,
                      }}
                    >
                      <IconPlaneDeparture size={15} aria-hidden />
                      Travels {trip.date} · {trip.when}
                    </Typography>
                  )}
                  {due && (
                    <Typography
                      variant="body2"
                      title={due.why}
                      sx={{ whiteSpace: "nowrap", fontWeight: due.late || due.soon ? 700 : 400, color: due.late ? toneColor("fail") : due.soon ? toneColor("warn") : "text.secondary" }}
                    >
                      {due.text}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ display: { xs: "none", xl: "block" } }}>
                    {data.destination} · {data.product}
                  </Typography>
                </Stack>
              </Box>
              {!wide && !phone && ownerBadge}
            </Stack>

            <Stack
              direction="row"
              alignItems="center"
              columnGap={1}
              rowGap={1}
              flexWrap={phone ? "nowrap" : "wrap"}
              sx={{ ml: "auto", justifyContent: "flex-end" }}
            >
              <CaseNav queue={queue} index={position} onMove={move} compact={phone} />
              {wide && ownerControls}
              {/* The next step is the main button; Change status is the
                  broad one, so it is secondary (QA nadia-21). */}
              {!phone && canEdit && (
                <ActionButton variant="outlined" onClick={() => setStatusOpen({})}>
                  Change status
                </ActionButton>
              )}
              {!phone && primary && (
                <ActionButton variant="contained" busyLabel={primary.busyLabel} onClick={primary.onClick}>
                  {primary.label}
                </ActionButton>
              )}
            </Stack>
          </Stack>
          <SectionBar sections={sections} onJump={jumpTo} />
        </Card>
      </Box>

      {freshCode ? (
        <CodeStrip
          code={freshCode}
          now={now}
          canEdit={canEdit}
          onUsed={async () => {
            try {
              await casesApi.codesUsed(data.id, dispatch);
              toast.success("Code marked as used.");
              await load(true);
            } catch (err) {
              toast.error(failureWords(err, "The code wasn’t marked as used. Try again."));
            }
          }}
        />
      ) : null}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ p: { xs: 2, sm: 2.5 }, "&:last-child": { pb: { xs: 2, sm: 2.5 } } }}>
          <Typography variant="h4" component="h2" sx={{ wordBreak: "break-word" }}>
            {data.destination} · {data.product}
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {metaLine.join(" · ")}
          </Typography>
          {partner && (
            <Typography variant="body2" mt={0.5}>
              Sent by <b>{partner}</b>
              {data.partner.reference ? ` · ref ${data.partner.reference}` : ""}. {partner} tells the traveller about every change.
            </Typography>
          )}
          {/* What the agent needs first, at a glance on every screen: the
              trip (in the header from tablet up), the appointment and what is
              still missing (QA karim-17). */}
          <Box component="dl" sx={{ m: 0, mt: 1, display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 1.5, rowGap: 0.25 }}>
            {trip && (
              <Box sx={{ display: { xs: "contents", sm: "none" } }}>
                <Typography component="dt" variant="body2" color="text.secondary">
                  Travels
                </Typography>
                <Typography component="dd" variant="body2" sx={{ m: 0, fontWeight: 600, color: tripUrgent ? "error.main" : "text.primary" }}>
                  {trip.date} · {trip.when}
                  {tripUrgent && trip.days >= 0 ? " · soon" : ""}
                </Typography>
              </Box>
            )}
            {appointment && (
              <>
                <Typography component="dt" variant="body2" color="text.secondary">
                  Appointment
                </Typography>
                <Typography component="dd" variant="body2" sx={{ m: 0, fontWeight: 600 }}>
                  {appointment}
                </Typography>
              </>
            )}
            {(whatsMissing.length > 0 || toReview > 0) && (
              <>
                <Typography component="dt" variant="body2" color="text.secondary">
                  {whatsMissing.length ? "Missing" : "To review"}
                </Typography>
                <Typography component="dd" variant="body2" sx={{ m: 0, fontWeight: 600 }}>
                  {[whatsMissing.length ? whatsMissing.join(", ") : null, whatsMissing.length && toReview ? `${plural(toReview, "document", "documents")} to review` : !whatsMissing.length ? plural(toReview, "document", "documents") : null]
                    .filter(Boolean)
                    .join(" · ")}
                </Typography>
              </>
            )}
            {ready && data.assigned ? (
              <>
                <Typography component="dt" variant="body2" color="text.secondary">
                  Owner
                </Typography>
                {/* Since when, for a handover (QA rania-14). */}
                <Typography component="dd" variant="body2" sx={{ m: 0, fontWeight: 600 }}>
                  {data.assigned.name}
                  <Box component="span" sx={{ fontWeight: 400, color: "text.secondary" }}>
                    {since ? `, since ${dayOfInstant(since)}` : ", since when wasn’t recorded"}
                  </Box>
                </Typography>
              </>
            ) : null}
            <Typography component="dt" variant="body2" color="text.secondary">
              Customer
            </Typography>
            <Box component="dd" sx={{ m: 0, display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: 1.5 }}>
              <CustomerLink data={data} name={name} onOpen={() => setCustomerOpen(true)} />
              {data.customer.id && canChats && !partner ? (
                <Link component={RouterLink} to={`/support?customer=${data.customer.id}`} variant="body2" fontWeight={600} sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                  <IconMessages size={16} aria-hidden />
                  Messages
                </Link>
              ) : null}
              {/* Their phone, and WhatsApp with the case number typed in (round 2, nadia-34). */}
              {!partner && data.customer.phone ? <PhoneLine phone={data.customer.phone} caseId={data.id} /> : null}
            </Box>
          </Box>
          {!wide && ownerControls && (
            <Stack direction="row" columnGap={1} rowGap={1} mt={2} alignItems="center" flexWrap="wrap" useFlexGap>
              {ownerControls}
            </Stack>
          )}
          {/* On a phone the next step's button is in its card just below,
              and Change status is the status chip in the header: each
              action once (karim-19). Without a next-step card, the main
              button stays here. */}
          {phone && canEdit && primary && !nextStepShown && (
            <Stack spacing={1} mt={1.5}>
              <ActionButton variant="contained" size="medium" fullWidth busyLabel={primary.busyLabel} onClick={primary.onClick}>
                {primary.label}
              </ActionButton>
            </Stack>
          )}
          {draft && (
            <Alert severity="info" sx={{ mt: 2 }}>
              The customer hasn’t submitted this application yet: the case can’t move as a whole. Change status moves one traveller at a time.
            </Alert>
          )}
          {!ready && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Owners, notes and document reviews switch on after database update 004.
            </Alert>
          )}
          {!canEdit && (
            <Alert severity="info" sx={{ mt: 2 }}>
              View only: your role can read cases but not change them.
            </Alert>
          )}
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={8}>
          {!wide && (
            <Stack spacing={2} mb={2}>
              {nextStepAlert}
              {decisionCard}
              {newCopiesAlert}
              {checks}
            </Stack>
          )}
          <Card variant="outlined" sx={{ p: { xs: 0.5, sm: "15px" } }}>
            {data.travellers.length > 1 && (
              <Tabs
                value={travellerIndex}
                onChange={(_, next) => setTab(next)}
                variant="scrollable"
                allowScrollButtonsMobile
                aria-label="Travellers"
                sx={{ px: 1, borderBottom: 1, borderColor: "divider", "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minHeight: 52 } }}
              >
                {data.travellers.map((t) => (
                  <Tab
                    key={t.request_id}
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <span>{travellerName(t)}</span>
                        <Box component="span" sx={{ fontFamily: "monospace", color: "text.secondary", fontWeight: 600 }}>
                          {travellerNumber(data, t)}
                        </Box>
                        {mixed && <TravellerStatusChip traveller={t} />}
                        <TabChip traveller={t} />
                      </Stack>
                    }
                  />
                ))}
              </Tabs>
            )}
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" mb={1.5} spacing={1}>
                <Typography variant="h5" component="h2">
                  {travellerName(traveller)}
                </Typography>
                {/* One number per application: traveller 2 of #1611 is 1611-2 (QA nadia-14). */}
                {data.travellers.length > 1 && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>
                    {travellerNumber(data, traveller)}
                  </Typography>
                )}
              </Stack>
              <TravellerProgress traveller={traveller} ready={ready} />
              {/* Documents first: reviewing them is the main work here (QA nadia-06). */}
              <Box id="case-documents" tabIndex={-1} sx={{ outline: "none" }}>
                <Documents
                  traveller={traveller}
                  travellerName={data.travellers.length > 1 ? travellerName(traveller) : undefined}
                  partner={partner}
                  canEdit={canEdit}
                  ready={ready}
                  onChanged={() => load(true)}
                  onEmail={() => setEmailOpen(true)}
                  emailLabel={partner ? `Send to ${partner} now` : undefined}
                  open={openDoc?.requestId === traveller.request_id ? openDoc : null}
                />
              </Box>
              <Box mt={3} pt={2.5} sx={{ borderTop: 1, borderColor: "divider" }}>
                <Visa
                  key={`${traveller.request_id}-${traveller.status}-${traveller.visa?.file || ""}`}
                  traveller={traveller}
                  travellerName={data.travellers.length > 1 ? travellerLabel(data, traveller) : undefined}
                  canEdit={canEdit}
                  onAdd={() => setDecide({ outcome: "approved", step: "visa" })}
                />
              </Box>
              <Box id="case-answers" tabIndex={-1} mt={3} pt={2.5} sx={{ borderTop: 1, borderColor: "divider", outline: "none" }}>
                <Answers key={traveller.request_id} traveller={traveller} officialTitle={data.official_form?.title} jump={jump} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          {/* On wide screens the next step stays beside the work, under the
              header, when the whole column fits; otherwise it scrolls with
              the page, so the Timeline is always reachable. */}
          <Stack
            ref={rail}
            spacing={2}
            sx={wide && railFits(railHeight, viewportHeight, topBar + headerHeight) ? { position: "sticky", top: topBar + headerHeight } : undefined}
          >
            {wide && nextStepAlert}
            {wide && decisionCard}
            {wide && newCopiesAlert}
            {wide && checks}
            <Payment caseData={data} visas={visas.byId} canEdit={canEdit} onChanged={() => Promise.all([load(true), visas.reload()])} />
            {/* Builder P: the on-time promise, red once missed, with its refund task. */}
            <PromiseCard caseData={data} canEdit={canEdit} onChanged={() => load(true)} />
            <Together caseData={data} canEdit={canEdit} onChanged={() => load(true)} />
            <CheckoutCase caseData={data} canEdit={canEdit} onChanged={() => load(true)} />
            <Filing
              caseData={data}
              canEdit={canEdit}
              onChanged={(filing) => {
                setState((s) => (s.data ? { ...s, data: { ...s.data, filing } } : s));
                load(true);
              }}
            />
            <HostCard caseData={data} canEdit={canEdit} onChanged={() => load(true)} />
            <PartyCards caseData={data} canEdit={canEdit} onChanged={() => load(true)} />
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" component="h2" mb={1}>
                  Customer
                </Typography>
                <CustomerLink data={data} name={name} onOpen={() => setCustomerOpen(true)} strong />
                {partner && (
                  <Typography variant="body2" color="text.secondary">
                    Sent by <b>{partner}</b>
                    {data.partner.reference ? (
                      <>
                        {" · ref "}
                        <Box component="span" sx={{ fontFamily: "monospace" }}>
                          {data.partner.reference}
                        </Box>
                      </>
                    ) : null}
                  </Typography>
                )}
                {data.customer.email && <CopyValue value={data.customer.email} label="email" />}
                {data.nationality && (
                  <Typography variant="body2" color="text.secondary">
                    Applying as: {data.nationality}
                  </Typography>
                )}
                {data.customer.id && canChats && !partner ? (
                  <ActionButton variant="text" component={RouterLink} to={`/support?customer=${data.customer.id}`} startIcon={<IconMessages size={16} />} sx={{ mt: 1, ml: -1.5 }}>
                    Support chat messages
                  </ActionButton>
                ) : null}
              </CardContent>
            </Card>
            <Notes
              notes={data.notes}
              canEdit={canEdit}
              ready={ready}
              onAdd={async (body) => {
                try {
                  await casesApi.note(data.id, body, dispatch);
                  toast.success(`Note added to case #${data.id}. Only the team sees it.`);
                  await load(true);
                  return true;
                } catch {
                  return false;
                }
              }}
              myId={signedInId}
              onEdit={async (note, body) => {
                try {
                  await casesApi.editNote(note.id, body, dispatch);
                  await load(true);
                  return true;
                } catch {
                  return false;
                }
              }}
              onDelete={(note) =>
                alert.show({
                  title: "Delete your note?",
                  message: `“${String(note.body).slice(0, 80)}${String(note.body).length > 80 ? "…" : ""}” goes for everyone. This can’t be undone; the timeline says a note was deleted.`,
                  type: "delete",
                  actions: [
                    { name: "Keep it", type: "cancel", callback: () => {} },
                    {
                      name: "Delete the note",
                      type: "delete",
                      callback: async () => {
                        try {
                          await casesApi.deleteNote(note.id, dispatch);
                          await load(true);
                        } catch {
                          /* the toast said why */
                        }
                      },
                    },
                  ],
                })
              }
            />
            <Insights caseData={data} />
            <Timeline data={data} />
          </Stack>
        </Grid>
      </Grid>

      {statusOpen && (
        <StatusDialog
          caseData={data}
          initial={statusOpen.initial}
          initialScope={statusOpen.scope}
          onClose={() => setStatusOpen(false)}
          onSave={saveStatus}
          onDecide={(outcome) => {
            setStatusOpen(false);
            setDecide({ outcome });
          }}
        />
      )}

      {emailReminder}

      {decide && (
        <RecordDecision
          caseData={data}
          start={decide}
          visas={visas.byId}
          guard={guard}
          unavailable={decisionsUnavailable}
          onClose={() => setDecide(null)}
          onUnavailable={() => setDecisionsUnavailable(true)}
          onDone={async ({ recorded }) => {
            setDecide(null);
            const [fresh] = await Promise.all([load(true), visas.reload()]);
            // A server without migration 023 saves the status and drops the
            // record: say so rather than let it look recorded.
            if (recorded && fresh && !fresh.decision) {
              setDecisionsUnavailable(true);
              toast.warn("The decision’s details weren’t kept: this server needs migration 023. The status is saved.");
            }
          }}
        />
      )}

      {receiptOpen && (
        <ReceiptViewer
          caseId={data.id}
          step={receiptOpen}
          canEdit={canEdit}
          onClose={() => setReceiptOpen(null)}
          onChanged={(filing) => {
            setState((s) => (s.data ? { ...s, data: { ...s.data, filing } } : s));
            load(true);
          }}
        />
      )}

      {unpaidDialog}

      {/* Leaving with new copies not emailed: email now, or leave (nadia-31). */}
      <Dialog open={leaving.open} onClose={leaving.onStay} fullWidth maxWidth="xs" aria-labelledby="unsent-title" aria-describedby="unsent-text">
        <DialogTitle id="unsent-title" sx={{ fontSize: "1.125rem", fontWeight: 700, pb: 1 }}>
          {unsentCopies === 1 ? "1 new copy not sent yet" : `${unsentCopies} new copies not sent yet`}
        </DialogTitle>
        <DialogContent>
          <Typography id="unsent-text" color="text.secondary">
            {partner ? `${partner} doesn’t know yet. Send it to them now?` : "The customer doesn’t know yet. Email them now? You see the email before it goes."}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <ActionButton variant="text" size="medium" onClick={leaving.onLeave}>
            Not now
          </ActionButton>
          <ActionButton
            variant="contained"
            size="medium"
            autoFocus
            onClick={() => {
              leaving.onStay();
              setEmailOpen(true);
            }}
          >
            {partner ? `Send to ${partner}` : "Email the customer"}
          </ActionButton>
        </DialogActions>
      </Dialog>
      {emailOpen && (
        <EmailNewCopiesDialog
          caseData={data}
          items={askedFor}
          onClose={() => setEmailOpen(false)}
          onSend={async () => {
            try {
              await casesApi.requestNewCopies(data.id, dispatch);
              await load(true);
              return true;
            } catch {
              return false;
            }
          }}
        />
      )}

      <CustomerPanel
        customer={customerOpen ? { id: data.customer.id, first_name: data.customer.first_name, last_name: data.customer.last_name, email: data.customer.email, image: data.customer.image, is_active: data.customer.is_active, phone: data.customer.phone || null } : null}
        currentCaseId={data.id}
        onClose={() => setCustomerOpen(false)}
        canEdit={false}
        canDelete={false}
        canChats={canChats}
      />
    </PageContainer>
  );
}

// The customer's name: a button that opens their record in place (the
// Customers panel, with every application they made, QA nadia-24), or plain
// text when this API doesn't send what the panel needs.
function CustomerLink({ data, name, onOpen, strong = false }) {
  const panel = data.customer?.id && data.customer.is_active !== undefined && data.customer.is_active !== null;
  if (!panel) return <Typography variant="body2" fontWeight={strong ? 600 : 400}>{name}</Typography>;
  return (
    <Link component="button" type="button" variant="body2" fontWeight={600} onClick={onOpen} aria-label={`${name} (open the customer record)`} sx={{ textAlign: "left", overflowWrap: "anywhere" }}>
      {name}
    </Link>
  );
}

CustomerLink.propTypes = { data: PropTypes.object.isRequired, name: PropTypes.string.isRequired, onOpen: PropTypes.func.isRequired, strong: PropTypes.bool };

Case.propTypes = { id: PropTypes.string.isRequired };

// A fresh page per case, so Next / Previous never shows the last case's tab,
// jump or half-typed note.
const CaseRoute = () => {
  const { id } = useParams();
  return <Case key={id} id={id} />;
};

export default CaseRoute;
